import { useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import {
  CheckIcon,
  ChevronDownIcon,
  CircleDashedIcon,
  CircleIcon,
  Clock3Icon,
  XIcon,
} from 'lucide-react';

import {
  ordinal,
  queuedReasonSentence,
  useWorkspaceConcurrencyLimit,
} from '@/features/concurrency';
import {
  useHoldZone,
  useInteractionHold,
  useLiveDuration,
  type ActiveRow,
  type AttentionKind,
  type QueuedRow,
} from '@/features/tasks';
import { useCanWork } from '@/features/workspaces/canWork';
import type { TaskView } from '@/lib/api/generated/model';
import { SECTION_HEADING } from '@/shared/components/sectionHeadingClass';
import { StepKindIcon } from '@/shared/components/StepKindIcon';
import { formatElapsedSince, formatExactTime } from '@/shared/utils/time';
import {
  breakdownWords,
  type InFlight,
  type InFlightChild,
  type InFlightChildState,
  type InFlightParent,
} from '@/features/workspace-overview/inFlight';
import {
  chosenParent,
  useInFlightDisclosure,
} from '@/features/workspace-overview/inFlightDisclosureStore';
import { runPageLink } from '@/features/workspace-overview/links';
import {
  OVERVIEW_STATE,
  overviewStateWord,
  ROW,
  ROW_DWELL,
  ROW_INTERACTIVE,
  ROW_META,
  ROW_TITLE,
  STRETCHED_LINK,
} from '@/features/workspace-overview/stateStyles';
import { useSteadyRows } from '@/features/workspace-overview/useSteadyRows';
import { queueWords } from '@/features/workspace-overview/yourTurn';
import { ProgressRing } from './ProgressRing';
import { LiveRing } from './LiveRing';
import { SubtaskCrumb } from './SubtaskCrumb';

/**
 * In motion (spec 153): what the system is doing without the person, each
 * piece of work once, as cards.
 *
 * - A parent waiting on its subtasks shows a progress ring (done of total,
 *   under brand waiver WD-07) and its batch in words. It opens to every
 *   subtask, read-only; a subtask in Your turn offers "Jump to it" there.
 *   Cards start closed (FR-008) and remember the person's choice.
 * - A running task shows how long it has run; a queued one its place.
 * - With nothing running or queued, one line says so, with the runs free.
 *
 * Cards hold their order while the pointer or focus is inside (spec 148).
 */

const ZONE = 'overview-in-motion';
/** The one tray every parent card controls (spec 156). */
const TRAY_ID = 'in-motion-tray';
const CARD =
  'border-border bg-card relative flex min-w-0 flex-col rounded-lg border';
const CARD_HEAD =
  'flex min-w-0 items-center gap-3 px-3 py-2.5 transition-colors hover:bg-accent/40';

export interface InMotionSectionProps {
  workspaceId: string;
  flight: InFlight;
  tasksById: ReadonlyMap<string, TaskView>;
  /** A workflow's key and whether it has a board, by its id. */
  workflows: ReadonlyMap<string, { key: string; published: boolean }>;
  /** How many tasks run now, for the idle line's "runs free". */
  running: number;
}

export function InMotionSection({
  workspaceId,
  flight,
  tasksById,
  workflows,
  running,
}: InMotionSectionProps) {
  const holdZone = useHoldZone(ZONE);
  const holding = useInteractionHold(
    (state) =>
      state.menusOpen > 0 ||
      state.pointerZone === ZONE ||
      state.focusZone === ZONE,
  );
  const rowId = useCallback((parent: InFlightParent) => parent.row.taskId, []);
  const rows = useSteadyRows(flight.rows, rowId, holding);
  const limit = useWorkspaceConcurrencyLimit();

  const anyRunning = rows.some(
    (parent) =>
      (!parent.queued && (parent.row as ActiveRow).state === 'running') ||
      parent.children.some((child) => child.state === 'running'),
  );
  const anyWork = rows.some(
    (parent) => parent.queued || (parent.row as ActiveRow).state === 'running',
  );

  // One parent's subtasks at a time, in a tray under the cards (spec 156):
  // the newest choice among the cards on screen.
  const choices = useInFlightDisclosure((state) => state.choices);
  const choose = useInFlightDisclosure((state) => state.choose);
  const parents = rows.filter(
    (parent) => !parent.queued && parent.children.length > 0,
  );
  const chosenId = chosenParent(
    choices,
    parents.map((parent) => parent.row.taskId),
  );
  const chosen = parents.find((parent) => parent.row.taskId === chosenId);

  return (
    <section
      aria-labelledby="overview-in-motion"
      className="flex flex-col gap-2.5"
    >
      <h2
        id="overview-in-motion"
        className={`flex items-center gap-1.5 ${SECTION_HEADING}`}
      >
        {anyRunning ? (
          <span
            aria-hidden="true"
            className="bg-board-state-running size-2 rounded-full motion-safe:animate-pulse"
          />
        ) : null}
        In motion
      </h2>
      {rows.length > 0 ? (
        // The cards and the tray hold still together (spec 148).
        <div className="flex flex-col gap-2.5" {...holdZone}>
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] items-start gap-2.5">
            {rows.map((parent) =>
              parent.queued ? (
                <QueuedCard
                  key={parent.row.taskId}
                  workspaceId={workspaceId}
                  row={parent.row as QueuedRow}
                />
              ) : parent.children.length > 0 ? (
                <ParentCard
                  key={parent.row.taskId}
                  workspaceId={workspaceId}
                  parent={parent}
                  chosen={parent.row.taskId === chosenId}
                  onToggle={(open) => {
                    choose(parent.row.taskId, open);
                  }}
                />
              ) : (
                <RunningCard
                  key={parent.row.taskId}
                  workspaceId={workspaceId}
                  row={parent.row as ActiveRow}
                />
              ),
            )}
          </ul>
          {chosen ? (
            <DetailTray
              workspaceId={workspaceId}
              parent={chosen}
              tasksById={tasksById}
              workflows={workflows}
              onClose={() => {
                const id = chosen.row.taskId;
                choose(id, false);
                // Back to the card that opened it, once the tray has gone.
                requestAnimationFrame(() => {
                  document
                    .querySelector<HTMLElement>(
                      `[data-motion-toggle="${CSS.escape(id)}"]`,
                    )
                    ?.focus();
                });
              }}
            />
          ) : null}
        </div>
      ) : null}
      {anyWork ? null : <IdleLine limit={limit} running={running} />}
      {flight.overflow > 0 ? (
        <Link
          to="/workspaces/$workspaceId/tasks"
          params={{ workspaceId }}
          search={{ state: 'running,queued' }}
          className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
        >
          {String(flight.overflow)} more in motion
        </Link>
      ) : null}
    </section>
  );
}

/**
 * Nothing runs and nothing waits for a run (FR-005). The runs free come from
 * the same limit as the header's meter; a service that reports no limit gets
 * the plain sentence, not a guess.
 */
function IdleLine({
  limit,
  running,
}: {
  limit: number | null;
  running: number;
}) {
  const free = limit === null ? null : Math.max(limit - running, 0);
  return (
    <p className="border-border text-muted-foreground rounded-lg border border-dashed px-3 py-2.5 text-xs">
      No agent is running
      {free === null
        ? null
        : ` · ${String(free)} ${free === 1 ? 'run' : 'runs'} free`}
    </p>
  );
}

function boardLink(workspaceId: string, workflowKey: string, taskId: string) {
  return {
    to: '/workspaces/$workspaceId/workflows/$workflowKey/board' as const,
    params: { workspaceId, workflowKey },
    search: { task: taskId, tab: 'details' as const },
  };
}

/**
 * A parent waiting on its subtasks: its ring, its step and its batch in
 * words. Its subtasks open in the tray under the cards, so opening one never
 * moves the others (spec 156). The whole card toggles: a stretched button
 * under the title's link.
 */
function ParentCard({
  workspaceId,
  parent,
  chosen,
  onToggle,
}: {
  workspaceId: string;
  parent: InFlightParent;
  chosen: boolean;
  onToggle: (open: boolean) => void;
}) {
  const canWork = useCanWork();
  const row = parent.row as ActiveRow;
  const count = parent.children.length;
  const total = row.children?.total ?? parent.batchTotal;
  const done = row.children ? total - row.children.open : 0;

  return (
    <li
      data-motion-card={row.taskId}
      data-chosen={chosen ? 'true' : undefined}
      className={`${CARD} ${chosen ? 'border-primary ring-primary ring-1' : ''}`}
    >
      <div className={CARD_HEAD}>
        <ProgressRing done={done} total={total} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {row.hasBoard ? (
            // The parent opens where its subtasks are: its run page.
            <Link
              {...runPageLink(workspaceId, row.workflowKey, row.taskId)}
              // `max-w-full`, not `self-start`: in this column flex, stretch
              // is what gives `truncate` a width to cut at. Sized to its own
              // content instead, the link grew past the card and the title
              // ran over the card beside it (spec 184). The focus ring still
              // ends with the text, because the link is a max-width box that
              // shrink-wraps its one line.
              className="text-foreground hover:text-primary focus-visible:ring-ring relative z-10 w-fit max-w-full truncate rounded-sm text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
            >
              {row.title}
            </Link>
          ) : (
            <span className="text-foreground truncate text-sm font-medium">
              {row.title}
            </span>
          )}
          <p className="text-muted-foreground truncate text-xs">
            {row.parent ? (
              <span className="relative z-10">
                <SubtaskCrumb workspaceId={workspaceId} parent={row.parent} />
              </span>
            ) : (
              row.workflowName
            )}
            {' · '}
            <StepKindIcon kind={row.stepKind} />{' '}
            {row.stepLabel ?? 'No step on this board'}
          </p>
          <p className="text-foreground/80 text-xs">
            {breakdownWords(parent.children, canWork)}
          </p>
        </div>
        <button
          type="button"
          data-motion-toggle={row.taskId}
          aria-expanded={chosen}
          aria-controls={TRAY_ID}
          aria-label={`${chosen ? 'Hide' : 'Show'} ${String(count)} ${count === 1 ? 'subtask' : 'subtasks'} of ${row.title}`}
          onClick={() => {
            onToggle(!chosen);
          }}
          // Stretched over the card, so a click anywhere but a link toggles.
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex size-8 shrink-0 items-center justify-center rounded-md after:absolute after:inset-0 after:rounded-lg focus-visible:ring-2 focus-visible:outline-none pointer-coarse:size-11"
        >
          <ChevronDownIcon
            aria-hidden="true"
            className={`size-4 motion-safe:transition-transform motion-safe:duration-[var(--motion-duration-base)] ${chosen ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {chosen ? (
        // Points from the chosen card to the tray below.
        <span
          aria-hidden="true"
          className="border-primary bg-card absolute -bottom-[7px] left-7 size-3 rotate-45 border-r border-b"
        />
      ) : null}
    </li>
  );
}

/**
 * The chosen parent's subtasks, under the cards (spec 156): every subtask,
 * read-only, as the card once listed them, two to a row where there is room.
 */
function DetailTray({
  workspaceId,
  parent,
  tasksById,
  workflows,
  onClose,
}: {
  workspaceId: string;
  parent: InFlightParent;
  tasksById: ReadonlyMap<string, TaskView>;
  workflows: ReadonlyMap<string, { key: string; published: boolean }>;
  onClose: () => void;
}) {
  const row = parent.row as ActiveRow;
  const count = parent.children.length;
  const total = row.children?.total ?? parent.batchTotal;
  const done = row.children ? total - row.children.open : 0;

  return (
    <section
      id={TRAY_ID}
      aria-label={`Subtasks of ${row.title}`}
      className="border-primary bg-card overflow-hidden rounded-lg border"
    >
      <div className="border-border flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-1.5 ps-3.5 pe-1.5">
        <p className="text-foreground text-sm font-medium">{row.title}</p>
        <p className="text-muted-foreground text-xs">
          {`${String(count)} ${count === 1 ? 'subtask' : 'subtasks'} · ${String(done)} of ${String(total)} done`}
        </p>
        {row.hasBoard ? (
          <Link
            {...runPageLink(workspaceId, row.workflowKey, row.taskId)}
            className="text-primary ms-auto text-xs underline-offset-4 hover:underline"
          >
            Open its run
          </Link>
        ) : null}
        <button
          type="button"
          aria-label="Close the subtasks"
          onClick={onClose}
          className={`text-muted-foreground hover:text-foreground focus-visible:ring-ring flex size-8 shrink-0 items-center justify-center rounded-md focus-visible:ring-2 focus-visible:outline-none pointer-coarse:size-11 ${row.hasBoard ? '' : 'ms-auto'}`}
        >
          <XIcon aria-hidden="true" className="size-4" />
        </button>
      </div>
      <ul className="grid sm:grid-cols-2 sm:gap-x-4">
        {parent.children.map((child) => (
          <ChildRow
            key={child.taskId}
            workspaceId={workspaceId}
            child={child}
            tasksById={tasksById}
            workflows={workflows}
          />
        ))}
      </ul>
    </section>
  );
}

/** A running task: "Running for 4m". */
function RunningCard({
  workspaceId,
  row,
}: {
  workspaceId: string;
  row: ActiveRow;
}) {
  // Counting every second, without a request (spec 155 FR-007).
  const elapsed = useLiveDuration(
    { started_at: row.startedAt, ended_at: null },
    true,
  );
  return (
    <li data-motion-card={row.taskId} className={CARD}>
      <div className={CARD_HEAD}>
        <LiveRing />
        <CardText workspaceId={workspaceId} row={row}>
          {row.startedAt && elapsed ? (
            <span title={formatExactTime(row.startedAt)}>
              Running for {elapsed}
            </span>
          ) : (
            'Running'
          )}
        </CardText>
      </div>
    </li>
  );
}

/** A task waiting for a run (spec 116): "Queued · 2nd", the reason on hover. */
function QueuedCard({
  workspaceId,
  row,
}: {
  workspaceId: string;
  row: QueuedRow;
}) {
  const limit = useWorkspaceConcurrencyLimit();
  const reason = queuedReasonSentence(row.reason, limit);
  return (
    <li data-motion-card={row.taskId} className={CARD}>
      <div className={CARD_HEAD}>
        <span className="flex size-11 shrink-0 items-center justify-center">
          <Clock3Icon
            aria-hidden="true"
            className="text-muted-foreground size-6"
            strokeWidth={1.5}
          />
        </span>
        <CardText workspaceId={workspaceId} row={row}>
          <span title={reason ?? undefined}>
            {row.position === null
              ? 'Queued'
              : `Queued · ${ordinal(row.position)}`}
          </span>
          {reason ? <span className="sr-only">{reason}</span> : null}
        </CardText>
      </div>
    </li>
  );
}

/** A running or queued card's title, where it stands, and its status line. */
function CardText({
  workspaceId,
  row,
  children,
}: {
  workspaceId: string;
  row: ActiveRow | QueuedRow;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      {row.hasBoard ? (
        <Link
          {...boardLink(workspaceId, row.workflowKey, row.taskId)}
          className="text-foreground hover:text-primary focus-visible:ring-ring truncate rounded-sm text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
        >
          {row.title}
        </Link>
      ) : (
        <span className="text-foreground truncate text-sm font-medium">
          {row.title}
        </span>
      )}
      <p className="text-muted-foreground truncate text-xs">
        {row.parent ? (
          <SubtaskCrumb workspaceId={workspaceId} parent={row.parent} />
        ) : (
          row.workflowName
        )}
        {' · '}
        <StepKindIcon kind={row.stepKind} />{' '}
        {row.stepLabel ?? 'No step on this board'}
      </p>
      <p className="text-foreground/80 text-xs">{children}</p>
    </div>
  );
}

/** A child's word; `waiting-on-you` is the reader's or the member's, below. */
const CHILD_WORD: Record<InFlightChildState, string> = {
  'needs-attention': 'Needs attention',
  'waiting-on-you': 'Waiting on you',
  running: 'Running',
  queued: 'Queued',
  'waiting-on-subtasks': 'Waiting on subtasks',
  'in-progress': 'In progress',
  'not-started': 'Not started',
  done: 'Done',
};

/** The attention row's own word, where the child has one: "Stopped". */
const KIND_WORD: Partial<Record<AttentionKind, string>> = {
  'failed-run': 'Run failed',
  'failed-workdir': 'Environment failed',
  orphaned: 'Off the board',
  stopped: 'Stopped',
  'spend-limit': 'Stopped by spend limit',
  'needs-account': 'Needs a personal account',
  'human-gate': 'Waiting for approval',
  branching: 'Waiting for a decision',
};

/** A child's mark: the four states' own, and quiet ones for the rest. */
function ChildMark({ state }: { state: InFlightChildState }) {
  const className = 'size-3.5 shrink-0';
  if (state === 'needs-attention') {
    const { Icon, text } = OVERVIEW_STATE['needs-attention'];
    return <Icon aria-hidden="true" className={`${className} ${text}`} />;
  }
  if (state === 'waiting-on-you') {
    const { Icon, text } = OVERVIEW_STATE['waiting-on-you'];
    return <Icon aria-hidden="true" className={`${className} ${text}`} />;
  }
  if (state === 'running') {
    const { Icon, text } = OVERVIEW_STATE.running;
    return (
      <Icon
        aria-hidden="true"
        className={`${className} ${text} motion-safe:animate-spin`}
      />
    );
  }
  if (state === 'waiting-on-subtasks') {
    const { Icon, text } = OVERVIEW_STATE['waiting-on-subtasks'];
    return <Icon aria-hidden="true" className={`${className} ${text}`} />;
  }
  if (state === 'done') {
    return (
      <CheckIcon
        aria-hidden="true"
        className={`${className} text-board-state-done`}
      />
    );
  }
  if (state === 'not-started') {
    return (
      <CircleDashedIcon
        aria-hidden="true"
        className={`${className} text-muted-foreground`}
      />
    );
  }
  if (state === 'queued') {
    return (
      <Clock3Icon
        aria-hidden="true"
        className={`${className} text-muted-foreground`}
      />
    );
  }
  return (
    <CircleIcon
      aria-hidden="true"
      className={`${className} text-muted-foreground`}
    />
  );
}

/** One subtask under its parent, acting in place where it can. */
function ChildRow({
  workspaceId,
  child,
  tasksById,
  workflows,
}: {
  workspaceId: string;
  child: InFlightChild;
  tasksById: ReadonlyMap<string, TaskView>;
  workflows: ReadonlyMap<string, { key: string; published: boolean }>;
}) {
  const canWork = useCanWork();
  const task = tasksById.get(child.taskId);
  const workflow = task ? workflows.get(task.workflow_id) : undefined;
  const link =
    workflow?.published === true
      ? boardLink(workspaceId, workflow.key, child.taskId)
      : null;
  const attention = child.attention;
  const word =
    (attention ? KIND_WORD[attention.kind] : undefined) ??
    (child.state === 'waiting-on-you'
      ? overviewStateWord(child.state, canWork)
      : CHILD_WORD[child.state]);
  const step =
    attention?.stepLabel ??
    child.active?.stepLabel ??
    child.queued?.stepLabel ??
    null;
  return (
    <li
      className={`${link ? ROW_INTERACTIVE : ROW} border-border border-t pl-4`}
    >
      <div className={`${ROW_TITLE} z-10`}>
        <ChildMark state={child.state} />
        {link ? (
          <Link {...link} className={STRETCHED_LINK}>
            {child.title}
          </Link>
        ) : (
          <span className="text-foreground truncate text-sm font-medium">
            {child.title}
          </span>
        )}
      </div>
      <p className={`${ROW_META} text-muted-foreground relative`}>
        <span className="text-foreground/80 font-medium">{word}</span>
        {step ? ` · ${step}` : null}
        {child.grandchildren ? (
          <span>
            {' · '}
            {String(child.grandchildren.done)} of{' '}
            {String(child.grandchildren.total)} done
          </span>
        ) : null}
        {/* On a phone the running time folds into this line (spec 150 US5). */}
        {child.active?.startedAt ? (
          <span className="sm:hidden">
            {' · '}
            {formatElapsedSince(child.active.startedAt)}
          </span>
        ) : null}
      </p>
      {child.active?.startedAt ? (
        <span className="hidden sm:contents">
          <Elapsed
            startedAt={child.active.startedAt}
            label={child.state === 'running' ? 'Running for' : 'Waiting for'}
            waiting={child.state !== 'running'}
          />
        </span>
      ) : null}
      {attention ? (
        // It is in Your turn, where its actions are: this row points there
        // rather than offering them twice (spec 152 FR-009).
        <div className="relative z-10 col-span-2 row-start-3 pt-1">
          <button
            type="button"
            aria-label={`Jump to ${child.title} in ${queueWords(canWork).heading}`}
            onClick={() => {
              jumpToQueue(child.taskId);
            }}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-sm text-xs underline underline-offset-4 focus-visible:ring-2 focus-visible:outline-none"
          >
            Jump to it
          </button>
        </div>
      ) : null}
    </li>
  );
}

/** The time at the right edge: "Running for 4m" or "Waiting for 12h". */
function Elapsed({
  startedAt,
  label,
  waiting = false,
}: {
  startedAt: string | null;
  label: string;
  waiting?: boolean;
}) {
  // Omitted, not placeheld: an em-dash in a duration slot reads as "measured,
  // and the answer was nothing".
  if (!startedAt) return null;
  return (
    <span
      title={formatExactTime(startedAt)}
      className={`${ROW_DWELL} ${
        waiting ? 'text-board-state-waiting' : 'text-board-state-running'
      } relative`}
    >
      <span className="text-muted-foreground block text-2xs font-normal tracking-wide uppercase">
        {label}
      </span>
      {formatElapsedSince(startedAt)}
    </span>
  );
}

/**
 * Brings a task's row in Your turn into view and onto its first control.
 * Nothing happens when the queue does not show it (past its first screenful);
 * the row's own title still opens the task.
 */
function jumpToQueue(taskId: string) {
  const row = document.querySelector<HTMLElement>(
    `li[data-queue-task="${CSS.escape(taskId)}"]`,
  );
  if (!row) return;
  // Optional: an environment without scrolling (a test DOM) still moves focus.
  row.scrollIntoView?.({ block: 'center' });
  row.querySelector<HTMLElement>('a, button')?.focus();
}
